/**
 * =============================================================================
 * Componente: ImportAffiliatesWizard
 *
 * Wizard premium (5 etapas) para importação em massa de afiliados via CSV.
 * Espelha a UX e a arquitetura do `ImportDealsWizard` de "Negócios", incluindo
 * a etapa de acompanhamento ao vivo e o botão de cancelamento dentro do step.
 *
 *   Etapas:
 *     1. Upload     — escolher CSV + DDI padrão.
 *     2. Validação  — checagem por linha (nome/e-mail obrigatórios, etc.).
 *     3. Importação — selecionar programa + aprovar imediatamente, executar.
 *     4. Progresso  — barra de progresso ao vivo + aba de erros + cancelar.
 *     5. Resultado  — resumo final do processamento.
 *
 * Prop `resume`:
 *   - { jobId, step: 'import' }   → reabre em jobs `awaiting_approval`,
 *                                    pulando upload/validação para o usuário
 *                                    apenas clicar "Iniciar importação".
 *   - { jobId, step: 'progress' } → reabre em jobs em andamento ou já
 *                                    finalizados para acompanhar progresso.
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v2.0.0 - Adiciona step Progresso (AffiliateProgressStep)
 *            espelhando Step6Progress de Negócios. Adiciona prop `resume`
 *            (Continuar aprovação / Ver progresso da listagem). Adiciona
 *            AlertDialog de "Cancelar importação" no próprio wizard.
 *   RELEASE: 2026-05-15 v1.0.0 - Versão inicial (4 etapas).
 * =============================================================================
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Users,
  Upload,
  CheckCircle2,
  Import,
  Award,
  Activity,
  Check,
  CheckCheck,
  FileSpreadsheet,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';

import AffiliateCSVUploadStep from './steps/AffiliateCSVUploadStep';
import AffiliateValidationStep from './steps/AffiliateValidationStep';
import AffiliateImportStep from './steps/AffiliateImportStep';
import AffiliateImportResultStep from './steps/AffiliateImportResultStep';
import AffiliateProgressStep from './steps/AffiliateProgressStep';

import { useAffiliateImport, AffiliateImportResult } from '@/hooks/useAffiliateImport';
import { useAffiliateImportJobs } from '@/hooks/useAffiliateImportJobs';
import { AffiliateCSVParseResult } from '@/lib/affiliate-csv-utils';

const log = createLogger('ImportAffiliatesWizard');

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pré-seleciona programa quando aberto a partir do detalhe de um programa. */
  defaultProgramId?: string;
  /**
   * Quando definido, abre o wizard num job existente, pulando upload/validação.
   * Usado pelas ações "Continuar aprovação" (step 'import') e "Ver progresso"
   * (step 'progress') da listagem em Listas → Afiliados.
   */
  resume?: { jobId: string; step: 'import' | 'progress' } | null;
}

type WizardStep = 'upload' | 'validation' | 'import' | 'progress' | 'result';

const STEPS: { key: WizardStep; label: string; icon: React.ElementType; description: string }[] = [
  { key: 'upload',     label: 'Upload',     icon: Upload,        description: 'Selecione o CSV com seus afiliados' },
  { key: 'validation', label: 'Validação',  icon: CheckCircle2,  description: 'Revise e corrija erros nos dados' },
  { key: 'import',     label: 'Importação', icon: Import,        description: 'Escolha o programa e dispare o lote' },
  { key: 'progress',   label: 'Progresso',  icon: Activity,      description: 'Acompanhe o processamento ao vivo' },
  { key: 'result',     label: 'Resultado',  icon: Award,         description: 'Resumo final do processamento' },
];

export function ImportAffiliatesWizard({ open, onOpenChange, defaultProgramId, resume }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { cancelJob } = useAffiliateImportJobs({ page: 1, pageSize: 1, enabled: false });
  const {
    validatedRows,
    validationStats,
    isValidating,
    isImporting,
    progress,
    validateRows,
    updateValidatedRow,
    importAffiliates,
    reset,
    fetchJobSummary,
    approveExistingJob,
    useJobDetails,
  } = useAffiliateImport();

  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [csvData, setCsvData] = useState<AffiliateCSVParseResult | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [defaultCountryCode, setDefaultCountryCode] = useState('55');
  const [programId, setProgramId] = useState<string>(defaultProgramId ?? '');
  const [forceApprove, setForceApprove] = useState(true);
  const [importResult, setImportResult] = useState<AffiliateImportResult | null>(null);
  // ID do job em acompanhamento (preenchido após handleStartImport ou ao resumir)
  const [jobId, setJobId] = useState<string | null>(null);
  // Diferencia jobs criados nesta sessão de jobs retomados via prop `resume`.
  // No modo retomado, bloqueamos retorno aos steps Upload/Validação porque o
  // CSV/rows já foram persistidos no servidor.
  const [isResumed, setIsResumed] = useState(false);
  // Controla o AlertDialog de confirmação de cancelamento (acionado pelo
  // botão "Cancelar importação" do AffiliateProgressStep).
  const [confirmCancelInWizard, setConfirmCancelInWizard] = useState(false);

  /**
   * Estado da etapa Upload: alimentado pelo callback `onReady` do step.
   * Habilita o botão "Continuar" do rodapé fixo do wizard.
   */
  const [uploadReady, setUploadReady] = useState<{
    data: AffiliateCSVParseResult;
    defaultCountryCode: string;
    sourceFile?: File;
  } | null>(null);

  /**
   * Estado da etapa Importação: alimentado pelo callback `onReadyChange` do step.
   * Habilita os botões "Fazer upgrade" / "Iniciar importação" no rodapé fixo.
   */
  const [importReady, setImportReady] = useState<{
    canStart: boolean;
    overQuota: boolean;
    openPaywall: () => void;
  }>({ canStart: false, overQuota: false, openPaywall: () => {} });

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const currentStepData = STEPS[currentStepIndex];
  const StepIcon = currentStepData.icon;

  const handleClose = useCallback(() => {
    reset();
    setCsvData(null);
    setSourceFile(null);
    setCurrentStep('upload');
    setProgramId(defaultProgramId ?? '');
    setForceApprove(true);
    setImportResult(null);
    setDefaultCountryCode('55');
    setUploadReady(null);
    setJobId(null);
    setIsResumed(false);
    setConfirmCancelInWizard(false);
    onOpenChange(false);
  }, [reset, onOpenChange, defaultProgramId]);

  // ---------------------------------------------------------------------------
  // Resume: ao abrir com prop `resume`, hidrata jobId/programId/forceApprove a
  // partir do banco e pula direto para o step indicado.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !resume) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await fetchJobSummary(resume.jobId);
        if (cancelled || !summary) return;
        setJobId(summary.id);
        setProgramId(summary.program_id ?? defaultProgramId ?? '');
        setForceApprove(!!summary.force_approve);
        setIsResumed(true);
        setCurrentStep(resume.step);
      } catch (err: any) {
        log.error('Falha ao retomar job de importação de afiliados', {
          jobId: resume.jobId,
          error: err?.message,
        });
        toast({
          title: 'Não foi possível abrir o job',
          description: err?.message ?? 'Erro desconhecido — feche e tente novamente.',
          variant: 'destructive',
          duration: 8000,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resume?.jobId, resume?.step]);

  /**
   * Avança da etapa Upload para Validação usando o payload preparado pelo step
   * (alimentado via callback `onReady`). Disparado pelo botão "Continuar" do
   * rodapé fixo do wizard.
   */
  const handleUploadContinue = async () => {
    if (!uploadReady) return;
    const { data, defaultCountryCode: countryCode, sourceFile: file } = uploadReady;
    setCsvData(data);
    setSourceFile(file ?? null);
    setDefaultCountryCode(countryCode);
    setCurrentStep('validation');
    await validateRows(data.rows, countryCode);
  };

  const handleValidationComplete = () => {
    if (validatedRows.some((v) => !v.isValid)) return;
    setCurrentStep('import');
  };

  const handleStartImport = async () => {
    if (!programId) return;
    const result = await importAffiliates({
      programId,
      validations: validatedRows,
      forceApprove,
      sourceFile: sourceFile ?? undefined,
      sourceFileName: sourceFile?.name,
      importName: sourceFile?.name?.replace(/\.[^.]+$/, '') || undefined,
      defaultCountryCode,
    });
    setImportResult(result);
    qc.invalidateQueries({ queryKey: ['referral-affiliates-paginated'] });
    qc.invalidateQueries({ queryKey: ['referral-affiliates-infinite'] });
    qc.invalidateQueries({ queryKey: ['affiliate-import-jobs'] });
    // Avança para o step de Progresso (não mais direto a Resultado): o usuário
    // acompanha contadores ao vivo e a transição para Resultado ocorre
    // automaticamente quando o job entra em estado terminal.
    if (result.success && result.jobId) {
      setJobId(result.jobId);
      setCurrentStep('progress');
    } else {
      // Falha já na enfileiramento: mostra direto o Resultado com os erros.
      setCurrentStep('result');
    }
  };

  /**
   * Reaproveita o pipeline de aprovação em jobs `awaiting_approval` retomados.
   * Chamado pelo botão "Iniciar importação" quando o wizard foi aberto pela
   * ação "Continuar aprovação" da listagem.
   */
  const handleApproveResumed = async () => {
    if (!jobId) return;
    try {
      const out = await approveExistingJob.mutateAsync(jobId);
      setImportResult({
        success: true,
        enqueued: true,
        jobId: out.jobId,
        queuedBatches: out.batches_created,
        queuedItems: out.items_enqueued,
        created: out.items_enqueued,
        approved: 0,
        skipped: 0,
        failed: 0,
        errors: [],
      });
      qc.invalidateQueries({ queryKey: ['affiliate-import-jobs'] });
      qc.invalidateQueries({ queryKey: ['affiliate-import-job-details', jobId] });
      setCurrentStep('progress');
    } catch (err: any) {
      toast({
        title: 'Falha ao iniciar importação',
        description: err?.message ?? 'Erro desconhecido',
        variant: 'destructive',
      });
    }
  };

  // Cancela o job em andamento (RPC cancel_affiliate_import_job).
  const handleConfirmCancel = async () => {
    if (!jobId) return;
    try {
      await cancelJob.mutateAsync(jobId);
      qc.invalidateQueries({ queryKey: ['affiliate-import-jobs'] });
      qc.invalidateQueries({ queryKey: ['affiliate-import-job-details', jobId] });
      toast({ title: 'Importação cancelada' });
    } catch (err: any) {
      toast({
        title: 'Erro ao cancelar',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    } finally {
      setConfirmCancelInWizard(false);
    }
  };

  // Polling de detalhes do job (somente no step 'progress')
  const jobDetailsQuery = useJobDetails(currentStep === 'progress' ? jobId : null);

  // Transição automática progress → result quando o job atinge estado terminal,
  // espelhando o comportamento do CRM (Step6Progress é o final do funil lá,
  // mas no Afiliados queremos mostrar o card "Resultado" idêntico ao atual).
  useEffect(() => {
    if (currentStep !== 'progress') return;
    const status = jobDetailsQuery.data?.job?.status;
    if (
      status === 'completed' ||
      status === 'completed_with_errors' ||
      status === 'failed' ||
      status === 'cancelled'
    ) {
      // Não força a transição se o usuário ainda está vendo erros — apenas
      // habilita o botão "Concluir" no rodapé do step progress. A transição
      // para o step 'result' é manual via clique no rodapé.
    }
  }, [currentStep, jobDetailsQuery.data?.job?.status]);

  const handleBack = () => {
    // No modo retomado bloqueia voltar (não há CSV em memória)
    if (isResumed) return;
    const prev = currentStepIndex - 1;
    if (prev >= 0) setCurrentStep(STEPS[prev].key);
  };

  const handleStepClick = (key: WizardStep) => {
    if (isResumed) return; // navegação por sidebar bloqueada no modo retomado
    const idx = STEPS.findIndex((s) => s.key === key);
    if (idx < currentStepIndex) setCurrentStep(key);
  };

  /**
   * Renderiza a step atual respeitando o contrato de layout do wizard:
   *   - A etapa "validation" gerencia o próprio overflow (tabela rolável
   *     internamente) e por isso fica em `h-full` puro.
   *   - As demais etapas (formulários curtos) ganham `overflow-y-auto`
   *     automático para o caso raro de viewport baixo, garantindo que o
   *     rodapé do wizard permaneça sempre visível.
   */
  const renderStep = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="h-full overflow-y-auto">
            <AffiliateCSVUploadStep onReady={setUploadReady} />
          </div>
        );
      case 'validation':
        return (
          <div className="h-full">
            <AffiliateValidationStep
              csvData={csvData}
              validatedRows={validatedRows}
              validationStats={validationStats}
              isValidating={isValidating}
              progress={progress}
              onUpdateRow={updateValidatedRow}
              onRevalidate={() => csvData && validateRows(csvData.rows, defaultCountryCode)}
              onComplete={handleValidationComplete}
            />
          </div>
        );
      case 'import':
        return (
          <div className="h-full overflow-y-auto">
            <AffiliateImportStep
              programId={programId}
              onProgramChange={setProgramId}
              forceApprove={forceApprove}
              onForceApproveChange={setForceApprove}
              validationStats={validationStats}
              isImporting={isImporting}
              progress={progress}
              onStartImport={handleStartImport}
              onReadyChange={setImportReady}
            />
          </div>
        );
      case 'progress':
        return (
          <div className="h-full">
            <AffiliateProgressStep
              isLoading={jobDetailsQuery.isLoading}
              details={jobDetailsQuery.data}
              jobId={jobId}
              error={jobDetailsQuery.error}
              onCancel={() => setConfirmCancelInWizard(true)}
              cancelPending={cancelJob.isPending}
            />
          </div>
        );
      case 'result':
        return (
          <div className="h-full overflow-y-auto">
            <AffiliateImportResultStep result={importResult} onComplete={handleClose} />
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          {/* Sidebar premium (mesmo padrão do ImportListWizard) */}
          <aside
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)' }}
          >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            <div className="relative z-10 flex flex-col h-full p-5">
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Importar Afiliados</h2>
                <p className="text-sm text-white/60 mt-1">Etapa {currentStepIndex + 1} de {STEPS.length}</p>
              </div>

              <nav className="flex-1 space-y-1">
                {STEPS.map((step, index) => {
                  const isCompleted = index < currentStepIndex;
                  const isActive = index === currentStepIndex;
                  const isPending = index > currentStepIndex;
                  const Icon = step.icon;
                  return (
                    <button
                      key={step.key}
                      type="button"
                      onClick={() => handleStepClick(step.key)}
                      disabled={isPending || isValidating || isImporting}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200',
                        isActive && 'bg-white/10 border border-white/20',
                        isCompleted && 'hover:bg-white/5 cursor-pointer',
                        isPending && 'opacity-50 cursor-not-allowed',
                        !isActive && !isPending && 'hover:bg-white/5',
                      )}
                    >
                      <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 border-2',
                        isCompleted && 'bg-green-500 border-green-500',
                        isActive && 'bg-white border-white',
                        isPending && 'bg-transparent border-white/30',
                      )}>
                        {isCompleted ? <Check className="h-4 w-4 text-white" /> : (
                          <Icon className={cn('h-4 w-4', isActive ? 'text-primary' : 'text-white/60')} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={cn('block text-sm font-medium truncate', isActive ? 'text-white' : 'text-white/80')}>
                          {step.label}
                        </span>
                        <span className="block text-xs text-white/40 truncate">{step.description}</span>
                      </div>
                    </button>
                  );
                })}
              </nav>

              <div className="mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">Resumo</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Linhas</span>
                    </div>
                    <span className="text-sm font-medium text-white">{csvData?.totalRows || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCheck className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Válidas</span>
                    </div>
                    <span className={cn('text-sm font-medium', (validationStats?.valid ?? 0) > 0 ? 'text-green-400' : 'text-white')}>
                      {validationStats?.valid || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="flex flex-col h-full min-h-0 min-w-0">
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">{currentStepData.label}</DialogTitle>
                  <DialogDescription className="text-sm">{currentStepData.description}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/*
              Área de conteúdo da step — NUNCA rola por aqui (overflow-hidden).
              Cada step decide internamente onde aplicar `overflow-auto`,
              garantindo que o rodapé do wizard nunca seja empurrado.
              Contrato: ver mem://ui/standardization/wizard-step-layout-contract.
            */}
            <div className="flex-1 min-h-0 min-w-0 overflow-hidden p-6">
              {renderStep()}
            </div>

            {/* Rodapé fixo do wizard — sempre visível, segura Voltar + Próximo */}
            <div className="flex-shrink-0 bg-background px-6 py-4 border-t flex justify-between items-center gap-3">
              <Button
                variant="outline"
                onClick={currentStepIndex === 0 ? handleClose : handleBack}
                disabled={isValidating || isImporting || currentStep === 'result'}
              >
                {currentStepIndex === 0 ? (
                  <><X className="h-4 w-4 mr-2" /> Cancelar</>
                ) : (
                  <><ChevronLeft className="h-4 w-4 mr-2" /> Voltar</>
                )}
              </Button>

              <div className="flex md:hidden items-center gap-1">
                {STEPS.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      'w-2 h-2 rounded-full transition-all',
                      index < currentStepIndex && 'bg-green-500',
                      index === currentStepIndex && 'bg-primary w-4',
                      index > currentStepIndex && 'bg-muted',
                    )}
                  />
                ))}
              </div>

              {/*
                Ação primária da etapa atual.
                - Upload: avança para Validação quando há CSV válido carregado.
                - Validação: avança para Importação quando não há erros.
                - Importação/Resultado: têm botão próprio dentro da step
                  (importação por causa da lógica de paywall/quota, e resultado
                  fecha pelo botão "Concluir" do próprio step).
              */}
              {currentStep === 'upload' && (
                <Button
                  onClick={handleUploadContinue}
                  disabled={!uploadReady || isValidating}
                >
                  Continuar <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}

              {currentStep === 'validation' && (
                <Button
                  onClick={handleValidationComplete}
                  disabled={
                    isValidating ||
                    validatedRows.length === 0 ||
                    validatedRows.some((v) => !v.isValid)
                  }
                >
                  {validatedRows.some((v) => !v.isValid)
                    ? 'Corrija os erros para continuar'
                    : <>Continuar <ChevronRight className="h-4 w-4 ml-2" /></>}
                </Button>
              )}

              {currentStep === 'import' && !isResumed && (
                <div className="flex items-center gap-2">
                  {importReady.overQuota && (
                    <Button variant="outline" onClick={importReady.openPaywall}>
                      <Sparkles className="h-4 w-4 mr-2" /> Fazer upgrade
                    </Button>
                  )}
                  <Button
                    onClick={handleStartImport}
                    disabled={!importReady.canStart || isImporting}
                  >
                    <Upload className="h-4 w-4 mr-2" /> Iniciar importação
                  </Button>
                </div>
              )}

              {/* Modo retomado (job em awaiting_approval): apenas aprova/enfileira */}
              {currentStep === 'import' && isResumed && (
                <Button
                  onClick={handleApproveResumed}
                  disabled={approveExistingJob.isPending}
                >
                  {approveExistingJob.isPending
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Iniciando...</>
                    : <><Upload className="h-4 w-4 mr-2" /> Iniciar importação</>}
                </Button>
              )}

              {currentStep === 'progress' && (() => {
                const status = jobDetailsQuery.data?.job?.status;
                const terminal = status === 'completed' || status === 'completed_with_errors'
                  || status === 'failed' || status === 'cancelled';
                return terminal ? (
                  <Button onClick={() => setCurrentStep('result')}>
                    Ver resultado <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handleClose}>
                    Fechar (continua em segundo plano)
                  </Button>
                );
              })()}

              {currentStep === 'result' && (
                <Button onClick={handleClose}>Concluir</Button>
              )}
            </div>
          </main>
        </div>

        {/* AlertDialog de confirmação para "Cancelar importação" dentro do step Progresso */}
        <AlertDialog open={confirmCancelInWizard} onOpenChange={setConfirmCancelInWizard}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar importação?</AlertDialogTitle>
              <AlertDialogDescription>
                Os itens já processados serão mantidos. Os lotes pendentes na fila
                serão descartados e o job marcado como cancelado. Esta ação não pode
                ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={cancelJob.isPending}>
                Voltar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmCancel}
                disabled={cancelJob.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {cancelJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Cancelar importação
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

export default ImportAffiliatesWizard;
